import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
  Grid,
  Skeleton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  AutoAwesome as SynthesisIcon,
  ExpandMore as ExpandIcon,
  Lightbulb as InsightIcon,
  Warning as GapIcon,
  TrendingUp as GrowthIcon,
  Description as DocumentIcon,
  Chat as MessageIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import ModeSelector from '../../components/Navigation/ModeSelector';
import { synthesisApi } from '../../services/api/synthesis.service';
import { TopicCluster, ClusterSummary, SynthesisInsights } from '../../types/synthesis.types';

/**
 * SynthesisPage
 *
 * AI-powered knowledge organization: clusters topics, generates
 * LLM summaries per cluster, and surfaces insights about
 * knowledge patterns.
 */
const SynthesisPage: React.FC = () => {
  const [clusters, setClusters] = useState<TopicCluster[]>([]);
  const [insights, setInsights] = useState<SynthesisInsights | null>(null);
  const [summaries, setSummaries] = useState<Record<string, ClusterSummary>>({});
  const [loadingSummary, setLoadingSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load clusters and insights on mount
   */
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [clustersResult, insightsResult] = await Promise.all([
        synthesisApi.clusterTopics('personal'),
        synthesisApi.getInsights('personal'),
      ]);
      setClusters(clustersResult);
      setInsights(insightsResult);
    } catch (err: any) {
      console.error('Error loading synthesis data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load synthesis data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate AI summary for a cluster
   */
  const handleGenerateSummary = useCallback(async (cluster: TopicCluster) => {
    setLoadingSummary(cluster.name);

    try {
      const summary = await synthesisApi.generateSummary(cluster.name, cluster.topicUids);
      setSummaries(prev => ({ ...prev, [cluster.name]: summary }));
    } catch (err: any) {
      console.error('Error generating summary:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate summary');
    } finally {
      setLoadingSummary(null);
    }
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Header */}
      <Paper
        elevation={0}
        style={{
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 12,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <ModeSelector size="small" showLabels />
          <Divider orientation="vertical" flexItem />
          <Stack direction="row" spacing={1} alignItems="center">
            <SynthesisIcon style={{ color: '#4DC4D2' }} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Synthesis Mode
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Content */}
      <Container maxWidth="lg" style={{ flex: 1, paddingTop: 24, paddingBottom: 24, overflow: 'auto' }}>
        {/* Error */}
        {error && (
          <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={300} style={{ borderRadius: 4 }} />
            </Grid>
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} variant="rectangular" height={80} style={{ borderRadius: 4 }} />
                ))}
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {/* Left Panel: Insights */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                style={{
                  padding: 16,
                  border: '1px solid rgba(0,0,0,0.12)',
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16, color: '#4DC4D2' }}>
                  Key Insights
                </Typography>

                {insights ? (
                  <Stack spacing={2}>
                    {/* Themes */}
                    <div>
                      <Stack direction="row" spacing={1} alignItems="center" style={{ marginBottom: 8 }}>
                        <InsightIcon style={{ fontSize: 18, color: '#4DC4D2' }} />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>Themes</Typography>
                      </Stack>
                      <List dense disablePadding>
                        {insights.themes.length > 0 ? insights.themes.map((theme, idx) => (
                          <ListItem key={idx} disableGutters style={{ paddingTop: 2, paddingBottom: 2 }}>
                            <ListItemText
                              primary={theme}
                              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                          </ListItem>
                        )) : (
                          <Typography variant="caption" color="text.secondary">No themes identified yet.</Typography>
                        )}
                      </List>
                    </div>

                    <Divider />

                    {/* Gaps */}
                    <div>
                      <Stack direction="row" spacing={1} alignItems="center" style={{ marginBottom: 8 }}>
                        <GapIcon style={{ fontSize: 18, color: '#E94257' }} />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>Knowledge Gaps</Typography>
                      </Stack>
                      <List dense disablePadding>
                        {insights.gaps.length > 0 ? insights.gaps.map((gap, idx) => (
                          <ListItem key={idx} disableGutters style={{ paddingTop: 2, paddingBottom: 2 }}>
                            <ListItemText
                              primary={gap}
                              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                          </ListItem>
                        )) : (
                          <Typography variant="caption" color="text.secondary">No significant gaps detected.</Typography>
                        )}
                      </List>
                    </div>

                    <Divider />

                    {/* Recent Growth */}
                    <div>
                      <Stack direction="row" spacing={1} alignItems="center" style={{ marginBottom: 8 }}>
                        <GrowthIcon style={{ fontSize: 18, color: '#325BA8' }} />
                        <Typography variant="body2" style={{ fontWeight: 600 }}>Recent Growth</Typography>
                      </Stack>
                      <List dense disablePadding>
                        {insights.recentGrowth.map((item, idx) => (
                          <ListItem key={idx} disableGutters style={{ paddingTop: 2, paddingBottom: 2 }}>
                            <ListItemText
                              primary={item}
                              primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </div>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No insights available yet.
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Right Panel: Topic Clusters */}
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 16 }}>
                Topic Clusters ({clusters.length})
              </Typography>

              {clusters.length === 0 ? (
                <Paper
                  elevation={0}
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 8,
                  }}
                >
                  <SynthesisIcon style={{ fontSize: 48, color: 'rgba(0,0,0,0.26)', marginBottom: 8 }} />
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No topic clusters found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start building your knowledge graph by chatting with your AI teammates and uploading documents.
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  {clusters.map((cluster) => (
                    <Accordion
                      key={cluster.name}
                      elevation={0}
                      style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}
                    >
                      <AccordionSummary expandIcon={<ExpandIcon />}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 16 }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                            {cluster.name}
                          </Typography>
                          <Chip
                            label={`${cluster.topicNames.length} topics`}
                            size="small"
                            style={{ height: 22, fontSize: '0.7rem', backgroundColor: 'rgba(77,196,210,0.15)', color: '#4DC4D2' }}
                          />
                          {cluster.documentCount > 0 && (
                            <Chip
                              icon={<DocumentIcon style={{ fontSize: 14 }} />}
                              label={`${cluster.documentCount}`}
                              size="small"
                              style={{ height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                          {cluster.messageCount > 0 && (
                            <Chip
                              icon={<MessageIcon style={{ fontSize: 14 }} />}
                              label={`${cluster.messageCount}`}
                              size="small"
                              style={{ height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Topic list */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                          {cluster.topicNames.map((name, idx) => (
                            <Chip
                              key={idx}
                              label={name}
                              size="small"
                              style={{
                                fontSize: '0.75rem',
                                backgroundColor: 'rgba(77,196,210,0.1)',
                                color: '#4DC4D2',
                                border: '1px solid rgba(77,196,210,0.3)',
                              }}
                            />
                          ))}
                        </div>

                        {/* Summary section */}
                        {summaries[cluster.name] ? (
                          <Paper
                            elevation={0}
                            style={{
                              padding: 16,
                              backgroundColor: 'rgba(77,196,210,0.04)',
                              border: '1px solid rgba(77,196,210,0.2)',
                              borderRadius: 4,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" style={{ marginBottom: 8 }}>
                              <AIIcon style={{ fontSize: 18, color: '#4DC4D2' }} />
                              <Typography variant="body2" style={{ fontWeight: 600, color: '#4DC4D2' }}>
                                AI Summary
                              </Typography>
                            </Stack>
                            <Typography variant="body2" style={{ marginBottom: 12, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                              {summaries[cluster.name].summary}
                            </Typography>

                            {summaries[cluster.name].keyPoints.length > 0 && (
                              <div style={{ marginBottom: 12 }}>
                                <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                  Key Points
                                </Typography>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {summaries[cluster.name].keyPoints.map((point, idx) => (
                                    <Chip
                                      key={idx}
                                      label={point}
                                      size="small"
                                      style={{ fontSize: '0.7rem', maxWidth: '100%' }}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {summaries[cluster.name].connections.length > 0 && (
                              <div>
                                <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                  Connections
                                </Typography>
                                <List dense disablePadding>
                                  {summaries[cluster.name].connections.map((conn, idx) => (
                                    <ListItem key={idx} disableGutters style={{ paddingTop: 0.8, paddingBottom: 0.8 }}>
                                      <ListItemIcon style={{ minWidth: 20 }}>
                                        <Typography variant="caption" color="text.secondary">-</Typography>
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={conn}
                                        primaryTypographyProps={{ variant: 'caption' }}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </div>
                            )}
                          </Paper>
                        ) : (
                          <Button
                            variant="outlined"
                            startIcon={loadingSummary === cluster.name ? <CircularProgress size={16} /> : <AIIcon />}
                            disabled={loadingSummary !== null}
                            onClick={() => handleGenerateSummary(cluster)}
                            style={{
                              borderColor: '#4DC4D2',
                              color: '#4DC4D2',
                            }}
                          >
                            {loadingSummary === cluster.name ? 'Generating...' : 'Generate AI Summary'}
                          </Button>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
        )}

        {/* Empty state (no data at all) */}
        {!loading && clusters.length === 0 && !error && !insights && (
          <div style={{ textAlign: 'center', paddingTop: 64, paddingBottom: 64 }}>
            <SynthesisIcon style={{ fontSize: 64, color: 'rgba(0,0,0,0.26)', marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Organize your knowledge
            </Typography>
            <Typography variant="body2" color="text.secondary" style={{ maxWidth: 480, margin: '0 auto' }}>
              Synthesis Mode uses AI to cluster your topics, generate summaries, and surface
              insights across your knowledge graph. Start by chatting with your AI teammates.
            </Typography>
          </div>
        )}
      </Container>
    </div>
  );
};

export default SynthesisPage;
